import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Loading, MessagePlugin } from 'tdesign-react';
import { fetchWechatAuthUrl, wechatLogin, type WechatLoginResult } from '@/services/portal';
import { setPortalToken } from '@/utils/auth';
import { usePortalAccount } from '@/contexts/PortalAccountContext';
import './Login.css';

/** sessionStorage key：本地保存的 state，回调时校验 CSRF */
const STATE_KEY = 'portal:wx_state';
/**
 * sessionStorage key：本次扫码跳转由哪个页面发起（login / register）。
 *
 * 原因：微信开放平台后台的回调域（WX_REDIRECT_URI）是一条固定 URL，无法为「登录」与
 * 「注册」各配一条。扫码跳转后无论从哪个页面触发，微信都会把 code 回传到同一个 URL
 * （当前约定是 `/portal/login`）。因此必须在跳转前把发起页写进 sessionStorage，
 * 回调时登录页读取该标记决定：
 *   - flow=register → 把 query 原样 forward 到 /portal/register 由注册页处理；
 *   - 否则直接走登录接口。
 */
const FLOW_KEY = 'portal:wx_flow';

/**
 * 生成 32 字符随机 state（CSRF 防护）
 */
const genState = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

/** 清掉回调 URL 上的 code/state，避免刷新页面再次触发登录 */
const cleanupQuery = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
};

/**
 * 登录页状态横幅数据：审批中 / 已拒绝 / 未注册
 */
interface LoginBannerState {
  status: 'PENDING' | 'REJECTED' | 'NOT_REGISTERED';
  nickname: string | null;
}

/**
 * 门户登录页（微信开放平台 - 网站应用扫码登录）
 *
 * 流程（纯登录，不再自动注册）：
 *  1) 「微信扫码登录」按钮 → 生成 state + 写入 flow=login → 调后端拿授权 URL → window.location 跳转
 *  2) 用户扫码 + 手机确认 → 微信回跳 WX_REDIRECT_URI?code=xxx&state=xxx
 *  3) 回调检测：
 *     - 如果 sessionStorage.flow=register（由注册页发起），原样 forward 到 /portal/register；
 *     - 否则校验 state → POST /portal/wechat/login：
 *       - status=APPROVED       → 写 token → 跳 /portal
 *       - status=PENDING        → 登录页提示「账号待启用」
 *       - status=REJECTED       → 登录页提示「账号已禁用」
 *       - status=NOT_REGISTERED → 登录页提示「未注册」+「去注册」按钮
 */
const PortalLogin = () => {
  const navigate = useNavigate();
  const { setAccount } = usePortalAccount();

  const [redirecting, setRedirecting] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [banner, setBanner] = useState<LoginBannerState | null>(null);

  const exchangedRef = useRef(false);

  /** 处理后端登录返回 */
  const handleLoginResult = useCallback(
    (res: WechatLoginResult): boolean => {
      if (res.status === 'APPROVED' && res.token && res.account) {
        setPortalToken(res.token);
        // account 只写内存 Context：刷新时会自动由 PortalGuard 重新拉 /portal/me，
        // 用户无法通过 DevTools 改 localStorage 伪造身份。
        setAccount({
          id: res.account.id,
          name: res.account.name,
          nickname: res.account.nickname,
          organization: res.account.organization,
        });
        MessagePlugin.success('登录成功');
        navigate('/portal', { replace: true });
        return true;
      }
      // PENDING / REJECTED / NOT_REGISTERED：在登录页展示
      setBanner({
        status: res.status as 'PENDING' | 'REJECTED' | 'NOT_REGISTERED',
        nickname: res.account?.nickname ?? null,
      });
      return false;
    },
    [navigate, setAccount],
  );

  const exchange = useCallback(
    async (code: string, state: string | null) => {
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      // CSRF 校验
      const savedState = sessionStorage.getItem(STATE_KEY);
      sessionStorage.removeItem(STATE_KEY);
      if (savedState && state && savedState !== state) {
        cleanupQuery();
        MessagePlugin.error('登录态校验失败，请重试');
        return;
      }

      setExchanging(true);
      try {
        const res = await wechatLogin(code, state ?? undefined);
        cleanupQuery();
        handleLoginResult(res);
      } catch {
        cleanupQuery();
        /* 拦截器统一提示 */
      } finally {
        setExchanging(false);
      }
    },
    [handleLoginResult],
  );

  // 检测微信回跳
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;

    // 判断是哪个页面发起的扫码；注册页触发的回调要 forward 给注册页处理
    const flow = sessionStorage.getItem(FLOW_KEY);
    if (flow === 'register') {
      // 不清除 state_key / flow_key——交给注册页去消费
      const target = `/portal/register${window.location.search}`;
      // 清空 URL 后 navigate，避免 history 里残留一条带 code 的登录页记录
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate(target, { replace: true });
      return;
    }

    // 正常登录流程才清掉 flow 标记（防御脏数据：历史写入未匹配）
    sessionStorage.removeItem(FLOW_KEY);
    void exchange(code, params.get('state'));
  }, [exchange, navigate]);

  const handleWechatLogin = async () => {
    setBanner(null);
    setRedirecting(true);
    try {
      const state = genState();
      sessionStorage.setItem(STATE_KEY, state);
      sessionStorage.setItem(FLOW_KEY, 'login');
      const { url } = await fetchWechatAuthUrl(state);
      window.location.assign(url);
    } catch {
      setRedirecting(false);
    }
  };

  // 产品侧收敛注册入口：登录页在未扫码时与扫码后发现"未注册"时，都不再暴露"去注册"按钮，
  // 统一只提示"账号未注册，无法登录"。注册页由其它入口（如管理员分享）引导。

  // 正在处理回调时不展示表单，只显示 loading
  if (exchanging) {
    return (
      <div className="portal-login-page">
        <div className="portal-login-card">
          <Loading text="正在完成登录..." />
        </div>
      </div>
    );
  }

  return (
    <div className="portal-login-page">
      <div className="portal-login-card">
        <div className="portal-login-title">指令提交系统 · 客户端</div>
        <div className="portal-login-sub">使用微信扫码授权登录</div>

        {/* —— 状态提示 —— */}
        {banner && <LoginBanner banner={banner} />}

        <div className="portal-login-form">
          <Button
            theme="primary"
            size="large"
            block
            loading={redirecting}
            onClick={handleWechatLogin}
          >
            {banner ? '重新扫码登录' : '微信扫码登录'}
          </Button>
          {/*
            注意：不在登录页展示「去注册」按钮与首次使用注册文案——产品侧默认收敛注册入口，
            仅在"扫码后发现未注册"的状态横幅里透出注册按钮（见 LoginBanner），
            避免普通用户在未扫码时直接跳到注册页造成账号冗余。
          */}
        </div>
      </div>
    </div>
  );
};

/** 登录页状态横幅：未注册 / 待启用 / 已禁用 */
const LoginBanner = ({ banner }: { banner: LoginBannerState }) => {
  if (banner.status === 'NOT_REGISTERED') {
    return (
      <div className="portal-login-audit portal-login-audit--rejected">
        <div className="portal-login-audit-title">账号未注册</div>
        <div className="portal-login-audit-desc">
          当前微信账号尚未在系统中注册，无法登录。请联系管理员开通后再试。
        </div>
      </div>
    );
  }

  const isPending = banner.status === 'PENDING';
  const title = isPending ? '账号待启用' : '账号已禁用';
  const desc = isPending
    ? '您的微信账号已提交，请联系管理员启用后再登录。'
    : '请联系管理员了解详情。';

  return (
    <div className={`portal-login-audit portal-login-audit--${isPending ? 'pending' : 'rejected'}`}>
      <div className="portal-login-audit-title">{title}</div>
      {banner.nickname && (
        <div className="portal-login-audit-nickname">微信昵称：{banner.nickname}</div>
      )}
      <div className="portal-login-audit-desc">{desc}</div>
    </div>
  );
};

export default PortalLogin;
